import React, { useEffect, useMemo, useRef, useState } from "react";
import { ref, get, remove } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import RecordMilkModal from "./RecordMilkModal";
import { logRecordAuditEvent } from "../../../utils/recordAuditLog";
import { BarChart, Bar, Cell, PieChart, Pie, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  DEFAULT_MILK_COLLECTION_PAGE_SIZE,
  getTotalPages,
  MILK_COLLECTION_PAGE_SIZE_OPTIONS,
  milkCollectionRangeForPage,
  paginateMilkCollectionItems,
} from "./milkCollectionPagination";

const ASSISTANT_NAME = "Amina"; // AI assistant accountant
const HANDWRITING_SERVICE_LABEL = "mysite backend OCR";
const HANDWRITING_SERVICE_DESC = "mysite backend service on Cloud Run";
const PIE_COLORS = ["#0891b2", "#0ea5e9", "#14b8a6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6"];
const litersTooltipFormatter = (value) => `${Number(value).toFixed(1)} L`;

/**
 * MilkCollectionTab
 * - date selector at top
 * - summary of totals collected by each collector (CollectedBy)
 * - left: table of farmers who HAVE a record for selected date (edit/delete)
 * - right: list of farmers who DON'T have a record for selected date (add record)
 *
 * Assumptions (based on your existing code):
 * - company milk records stored at: Companies/{companyName}/MilkCollected/{y}/{m}/{d}/{recordId}
 * - farmer roots are under: Companies/{companyName}/FarmerRecords/{otherName}/... (used to build farmer list)
 * - record.UserName === farmer.otherName
 */
const MilkCollectionTab = ({ companyName }) => {
  const MIN_MILK_QTY = 0.1;
  const MAX_MILK_QTY = 1000;
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  const [farmers, setFarmers] = useState([]); // { otherName, name, profile }
  const [recordsMap, setRecordsMap] = useState({}); // map userName -> { record, id }
  const [loading, setLoading] = useState(false);
  const [modalPrefillTotal, setModalPrefillTotal] = useState(null);
  const [withRecordsPageSize, setWithRecordsPageSize] = useState(DEFAULT_MILK_COLLECTION_PAGE_SIZE);
  const [withoutRecordsPageSize, setWithoutRecordsPageSize] = useState(DEFAULT_MILK_COLLECTION_PAGE_SIZE);
  const [withRecordsPage, setWithRecordsPage] = useState(1);
  const [withoutRecordsPage, setWithoutRecordsPage] = useState(1);

  // modal state
  const [openModal, setOpenModal] = useState(false);
  const [modalFarmer, setModalFarmer] = useState(null);
  const [modalExisting, setModalExisting] = useState(null); // existing record if editing
  const [handModalFarmer, setHandModalFarmer] = useState(null);
  const [voiceModalFarmer, setVoiceModalFarmer] = useState(null);
  const [handValue, setHandValue] = useState("");
  const [voiceValue, setVoiceValue] = useState("");
  const [voiceStatus, setVoiceStatus] = useState("idle"); // idle | listening | error | unsupported
  const voiceTargetRef = useRef("voiceModal");
  const recognitionRef = useRef(null);
  const lastAssistantSpeechRef = useRef(0);
  const [assistantActive, setAssistantActive] = useState(false);
  const [assistantIndex, setAssistantIndex] = useState(0);
  const [assistantValue, setAssistantValue] = useState("");
  const [handLoopActive, setHandLoopActive] = useState(false);
  const [handLoopIndex, setHandLoopIndex] = useState(0);
  const [handLoopValue, setHandLoopValue] = useState("");

  // Derived lists
  const farmersWithRecord = useMemo(() => {
    return farmers.filter((f) => recordsMap[f.otherName]);
  }, [farmers, recordsMap]);

  const farmersWithoutRecord = useMemo(() => {
    return farmers.filter((f) => !recordsMap[f.otherName]);
  }, [farmers, recordsMap]);

  const assistantFarmer = useMemo(() => {
    if (!farmersWithoutRecord.length) return null;
    const safeIndex = Math.min(assistantIndex, farmersWithoutRecord.length - 1);
    return farmersWithoutRecord[safeIndex];
  }, [assistantIndex, farmersWithoutRecord]);

  const handLoopFarmer = useMemo(() => {
    if (!farmersWithoutRecord.length) return null;
    const safeIndex = Math.min(handLoopIndex, farmersWithoutRecord.length - 1);
    return farmersWithoutRecord[safeIndex];
  }, [farmersWithoutRecord, handLoopIndex]);

  const farmersWithoutRecordCount = farmersWithoutRecord.length;

  // totals by collector (CollectedBy)
  const totalsByCollector = useMemo(() => {
    const totals = {};
    Object.values(recordsMap).forEach(({ record }) => {
      const kgs = parseFloat(record?.TotalKgs || "0") || 0;
      const c = record?.CollectedBy || record?.UserName || "unknown";
      totals[c] = (totals[c] || 0) + kgs;
    });
    return totals;
  }, [recordsMap]);

  const totalLiters = useMemo(() => {
    return Object.values(recordsMap).reduce((sum, entry) => {
      return sum + (parseFloat(entry?.record?.TotalKgs || "0") || 0);
    }, 0);
  }, [recordsMap]);

  const averageLitersPerFarmer = farmersWithRecord.length ? totalLiters / farmersWithRecord.length : 0;
  const coveragePercent = farmers.length ? (farmersWithRecord.length / farmers.length) * 100 : 0;

  const litersByFarmerData = useMemo(() => {
    return farmersWithRecord
      .map((farmer) => {
        const entry = recordsMap[farmer.otherName];
        return {
          farmerName: getFarmerDisplayName(farmer),
          liters: parseFloat(entry?.record?.TotalKgs || "0") || 0,
        };
      })
      .sort((a, b) => b.liters - a.liters)
      .slice(0, 10);
  }, [farmersWithRecord, recordsMap]);

  const collectorPieData = useMemo(() => {
    return Object.entries(totalsByCollector).map(([name, value]) => ({
      name,
      value: Number(value.toFixed(1)),
    }));
  }, [totalsByCollector]);

  const withRecordsTotalPages = getTotalPages(farmersWithRecord, withRecordsPageSize);
  const withoutRecordsTotalPages = getTotalPages(farmersWithoutRecord, withoutRecordsPageSize);

  const paginatedFarmersWithRecord = useMemo(() => {
    return paginateMilkCollectionItems(farmersWithRecord, withRecordsPage, withRecordsPageSize);
  }, [farmersWithRecord, withRecordsPage, withRecordsPageSize]);

  const paginatedFarmersWithoutRecord = useMemo(() => {
    return paginateMilkCollectionItems(farmersWithoutRecord, withoutRecordsPage, withoutRecordsPageSize);
  }, [farmersWithoutRecord, withoutRecordsPage, withoutRecordsPageSize]);

  const withRecordsRange = useMemo(() => {
    return milkCollectionRangeForPage(farmersWithRecord, withRecordsPage, withRecordsPageSize);
  }, [farmersWithRecord, withRecordsPage, withRecordsPageSize]);

  const withoutRecordsRange = useMemo(() => {
    return milkCollectionRangeForPage(farmersWithoutRecord, withoutRecordsPage, withoutRecordsPageSize);
  }, [farmersWithoutRecord, withoutRecordsPage, withoutRecordsPageSize]);

  // Helper to parse selectedDate -> y,m,d (numbers/strings as saved)
  const parseDateParts = (dateStr) => {
    const dObj = new Date(dateStr);
    const y = dObj.getFullYear();
    const m = dObj.getMonth() + 1; // numeric month
    const d = dObj.getDate();
    return { y: String(y), m: String(m), d: String(d) };
  };

  const nextLoopIndex = (currentIndex, listLength) => {
    if (listLength <= 0) return 0;
    return currentIndex + 1 >= listLength ? 0 : currentIndex + 1;
  };

  const buildAssistantSpeechLine = (farmerName) =>
    `${ASSISTANT_NAME} here. Next farmer without a record is ${farmerName}. Please provide the milk amount.`;

  function getFarmerDisplayName(farmer) {
    if (!farmer) return "";
    const profile = farmer.profile || {};
    return profile.CloudName || farmer.CloudName || farmer.name || profile.name || farmer.otherName || "";
  }

  // Load farmers list (try Company/{companyName}/FarmerRecords root and extract profile if present)
  const loadFarmers = async () => {
    if (!companyName) {
      setFarmers([]);
      return;
    }
    try {
      const p = `Companies/${companyName}/FarmerRecords`;
      const snap = await get(ref(database, p));
      const val = snap.val() || {};
      // val may contain per-farmer profile keys OR only year keys.
      const list = Object.keys(val).map((otherName) => {
        const node = val[otherName] || {};
        // try to read display name
        const display = getFarmerDisplayName({ otherName, profile: node, CloudName: node.CloudName, name: node.name });
        return { otherName, name: display, profile: node };
      });
      // sort alphabetically by display name
      list.sort((a, b) => a.name.localeCompare(b.name));
      setFarmers(list);
    } catch (err) {
      console.error("loadFarmers error", err);
      setFarmers([]);
    }
  };

  // Load milk records for the selected date
  const loadRecordsForDate = async (dateStr) => {
    if (!companyName || !dateStr) {
      setRecordsMap({});
      return;
    }
    setLoading(true);
    try {
      const { y, m, d } = parseDateParts(dateStr);
      // The app saves under Companies/{companyName}/MilkCollected/{y}/{m}/{d}/{key}
      const path = `Companies/${companyName}/MilkCollected/${y}/${m}/${d}`;
      const snap = await get(ref(database, path));
      const val = snap.val() || {};
      // val: id -> record (contains UserName)
      const map = {};
      Object.entries(val).forEach(([id, record]) => {
        // prefer proper userName if present
        const keyName = record?.UserName || record?.User || record?.FarmerId || id;
        map[keyName] = { id, record };
      });
      setRecordsMap(map);
    } catch (err) {
      console.error("loadRecordsForDate error", err);
      setRecordsMap({});
    } finally {
      setLoading(false);
    }
  };

  // initial load: farmers + records for default date
  useEffect(() => {
    if (!companyName) return;
    loadFarmers();
    loadRecordsForDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]);

  // reload records whenever date changes
  useEffect(() => {
    if (!companyName) return;
    loadRecordsForDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, companyName]);

  useEffect(() => {
    setWithRecordsPage(1);
    setWithoutRecordsPage(1);
  }, [selectedDate]);

  useEffect(() => {
    setWithRecordsPage((page) => Math.min(page, withRecordsTotalPages));
  }, [withRecordsTotalPages]);

  useEffect(() => {
    setWithoutRecordsPage((page) => Math.min(page, withoutRecordsTotalPages));
  }, [withoutRecordsTotalPages]);

  // keep loop indices in range when list shrinks
  useEffect(() => {
    setAssistantIndex((idx) => (idx >= farmersWithoutRecordCount ? 0 : idx));
    setHandLoopIndex((idx) => (idx >= farmersWithoutRecordCount ? 0 : idx));
  }, [farmersWithoutRecordCount]);

  // open modal to add record for farmer
  const handleAddForFarmer = (farmer, prefillTotal = null) => {
    setModalFarmer(farmer);
    setModalExisting(null);
    setModalPrefillTotal(prefillTotal);
    setOpenModal(true);
  };

  // open modal to edit existing record
  const handleEditRecord = (farmer, recEntry) => {
    setModalFarmer(farmer);
    setModalExisting({ id: recEntry.id, ...recEntry.record });
    setModalPrefillTotal(null);
    setOpenModal(true);
  };

  // delete record: remove from MilkCollected and NewCollectedMilkRecord paths and FarmerRecords date node
  const handleDeleteRecord = async (farmer, recEntry) => {
    if (!companyName || !recEntry) return;
    if (!window.confirm(`Delete milk record for ${farmer.name} on ${selectedDate}? This cannot be undone.`)) return;

    try {
      const { y, m, d } = parseDateParts(selectedDate);
      const key = recEntry.id;
      const pathsToRemove = [
        `Companies/${companyName}/MilkCollected/${y}/${m}/${d}/${key}`,
        `Companies/${companyName}/NewCollectedMilkRecord/${key}`,
        `NewCollectedMilkRecord/${key}`,
        // remove the FarmerRecords day node if present
        `Companies/${companyName}/FarmerRecords/${farmer.otherName}/${y}/${m}/${d}`,
      ];
      await Promise.all(
        pathsToRemove.map(async (p) => {
          // use remove via ref(database, p)
          try {
            await remove(ref(database, p));
          } catch (e) {
            // ignore missing path
          }
        })
      );
      await logRecordAuditEvent({
        action: "delete",
        entityType: "milk_collection_record",
        companyName,
        recordId: key,
        sourceComponent: "MilkCollectionTab",
        oldData: {
          ...(recEntry?.record || {}),
          farmerOtherName: farmer?.otherName || null,
          farmerName: farmer?.name || null,
          recordId: key,
          selectedDate,
        },
      });
      toast.success("Record deleted.");
      // reload records
      await loadRecordsForDate(selectedDate);
    } catch (err) {
      console.error("delete record", err);
      toast.error("Failed to delete record.");
    }
  };

  // callback when modal saves a new/edited record -> reload lists
  const onModalSaved = async () => {
    await loadFarmers();
    await loadRecordsForDate(selectedDate);
    setOpenModal(false);
    setModalPrefillTotal(null);
  };

  // Gracefully stop any active speech recognition session. Safe to call multiple times.
  const stopSpeechRecognition = () => {
    let hadError = false;
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } catch (e) {
      console.error("Failed to stop speech recognition", e);
      setVoiceStatus("error");
      hadError = true;
    }
    recognitionRef.current = null;
    if (!hadError) setVoiceStatus("idle");
  };

  const handleOpenHandModal = (farmer) => {
    setHandModalFarmer(farmer);
    setHandValue("");
  };

  const handleOpenVoiceModal = (farmer) => {
    setVoiceModalFarmer(farmer);
    setVoiceValue("");
    setVoiceStatus("idle");
  };

  const handleCloseHandModal = () => {
    setHandModalFarmer(null);
    setHandValue("");
  };

  const handleCloseVoiceModal = () => {
    stopSpeechRecognition();
    setVoiceModalFarmer(null);
    setVoiceValue("");
  };

  const startListening = (target = "voiceModal") => {
    const normalizedTarget = target === "assistant" || target === "voiceModal" ? target : "voiceModal";
    voiceTargetRef.current = normalizedTarget;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceStatus("unsupported");
      return;
    }
    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.lang = "en-US";
      recognition.interimResults = false;
      recognition.onresult = (event) => {
        const transcript = event?.results?.[0]?.[0]?.transcript || "";
        const numericMatch = transcript.match(/\d+(\.\d+)?/);
        if (numericMatch) {
          const captured = numericMatch[0];
          if (voiceTargetRef.current === "assistant") {
            setAssistantValue(captured);
          } else {
            setVoiceValue(captured);
          }
        } else {
          if (voiceTargetRef.current === "assistant") {
            setAssistantValue("");
          } else {
            setVoiceValue("");
          }
          toast.error("No number detected. Please speak the milk amount again.");
          setVoiceStatus("error");
        }
      };
      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event);
        setVoiceStatus("error");
      };
      recognition.onend = () => {
        recognitionRef.current = null;
        setVoiceStatus((prev) => (prev === "error" ? prev : "idle"));
      };
      recognitionRef.current = recognition;
      setVoiceStatus("listening");
      recognition.start();
    } catch (e) {
      setVoiceStatus("error");
    }
  };

  const stopListening = () => {
    stopSpeechRecognition();
  };

  const continueWithValue = (farmer, value) => {
    const numeric = parseFloat(value);
    if (Number.isNaN(numeric)) {
      toast.error("Enter a valid milk quantity before continuing.");
      return;
    }
    if (numeric < MIN_MILK_QTY) {
      toast.error(`Milk quantity must be at least ${MIN_MILK_QTY} kgs.`);
      return;
    }
    if (numeric > MAX_MILK_QTY) {
      toast.error("Quantity looks too high. Please confirm and try again.");
      return;
    }
    setHandModalFarmer(null);
    setVoiceModalFarmer(null);
    setHandValue("");
    setVoiceValue("");
    stopSpeechRecognition();
    handleAddForFarmer(farmer, numeric);
  };

  const handleStartAssistant = () => {
    setAssistantActive(true);
    setAssistantIndex(0);
    setAssistantValue("");
  };

  const handleStopAssistant = () => {
    setAssistantActive(false);
    setAssistantValue("");
    stopSpeechRecognition();
  };

  const handleStartHandLoop = () => {
    setHandLoopActive(true);
    setHandLoopIndex(0);
    setHandLoopValue("");
  };

  const handleStopHandLoop = () => {
    setHandLoopActive(false);
    setHandLoopValue("");
  };

  const handleHandLoopSend = () => {
    if (!handLoopFarmer) return;
    continueWithValue(handLoopFarmer, handLoopValue);
    setHandLoopValue("");
    setHandLoopIndex((prev) => nextLoopIndex(prev, farmersWithoutRecordCount));
  };

  // announce next farmer name using browser speech synthesis (best effort)
  useEffect(() => {
    if (!assistantActive || !assistantFarmer) return;
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const now = Date.now();
    if (now - lastAssistantSpeechRef.current < 500) return;
    lastAssistantSpeechRef.current = now;
    const synth = window.speechSynthesis;
    const spokenName = getFarmerDisplayName(assistantFarmer);
    const utterance = new SpeechSynthesisUtterance(buildAssistantSpeechLine(spokenName));
    try {
      if (synth.speaking || synth.paused) {
        synth.cancel();
      }
      synth.speak(utterance);
    } catch (e) {
      console.error("Speech synthesis failed", e);
    }
  }, [assistantActive, assistantFarmer]);

  // keyboard shortcuts for assistant flow
  useEffect(() => {
    if (!assistantActive) return;
    const handler = (e) => {
      if (!assistantFarmer) return;
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA" || e.target?.isContentEditable) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        continueWithValue(assistantFarmer, assistantValue);
        setAssistantValue("");
        setAssistantIndex((prev) => nextLoopIndex(prev, farmersWithoutRecordCount));
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        toast.error("Value declined. Capture again to proceed.");
        setAssistantValue("");
      } else if (e.key === " ") {
        e.preventDefault();
        setAssistantValue("");
        setAssistantIndex((prev) => nextLoopIndex(prev, farmersWithoutRecordCount));
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [assistantActive, assistantFarmer, assistantValue, farmersWithoutRecordCount]);

  return (
    <div className="p-6">
      <div className="mb-4 space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-cyan-700 mb-1">🥛 Milk Collection</h2>
          <p className="text-gray-600 mb-3">View and manage daily milk collection. Pick a date to inspect records and manage entries.</p>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-gray-600 block">Select date</label>
            <input
              type="date"
              value={selectedDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600 block">Company</label>
            <div className="text-sm font-medium">{companyName || "—"}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="bg-white border rounded-lg p-3">
            <div className="text-xs text-gray-500">Total liters</div>
            <div className="text-2xl font-semibold text-cyan-700">{totalLiters.toFixed(1)}</div>
          </div>
          <div className="bg-white border rounded-lg p-3">
            <div className="text-xs text-gray-500">Recorded farmers</div>
            <div className="text-2xl font-semibold text-slate-800">{farmersWithRecord.length}</div>
          </div>
          <div className="bg-white border rounded-lg p-3">
            <div className="text-xs text-gray-500">Avg liters / farmer</div>
            <div className="text-2xl font-semibold text-emerald-700">{averageLitersPerFarmer.toFixed(1)}</div>
          </div>
          <div className="bg-white border rounded-lg p-3">
            <div className="text-xs text-gray-500">Daily coverage</div>
            <div className="text-2xl font-semibold text-indigo-700">{coveragePercent.toFixed(0)}%</div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <div
            className="bg-white border rounded-lg p-3 xl:col-span-2"
            aria-label={`Bar chart showing liters collected by farmer on ${selectedDate}`}
          >
            <div className="text-sm font-semibold text-gray-800 mb-2">Liters by farmer ({selectedDate})</div>
            <div className="h-64">
              {litersByFarmerData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={litersByFarmerData}>
                    <XAxis dataKey="farmerName" tick={{ fontSize: 11 }} interval={0} angle={-35} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={litersTooltipFormatter} labelFormatter={(label) => `Farmer: ${label}`} />
                    <Bar dataKey="liters" fill="#0891b2" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">No farmer records for this date.</div>
              )}
            </div>
          </div>

          <div className="bg-white border rounded-lg p-3">
            <div className="text-sm font-semibold text-gray-800 mb-2">Collector share ({selectedDate})</div>
            <div className="h-64">
              {collectorPieData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart role="img" aria-label={`Collector distribution by milk volume on ${selectedDate}`}>
                    <Pie
                      data={collectorPieData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={85}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {collectorPieData.map((entry, index) => (
                        <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={litersTooltipFormatter} labelFormatter={(label) => `Collector: ${label}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">No collections yet.</div>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-600 block">Collectors totals</label>
          <div className="bg-white border rounded px-3 py-2 text-sm">
            {Object.keys(totalsByCollector).length === 0 ? (
              <span className="text-gray-500">No collections yet</span>
            ) : (
              <div className="space-y-1">
                {Object.entries(totalsByCollector).map(([collector, sum]) => (
                  <div key={collector} className="flex justify-between">
                    <div className="truncate">{collector}</div>
                    <div className="font-semibold text-cyan-700">{Number(sum).toFixed(1)} Kgs</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Grid layout for four main activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Farmers with record */}
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <div className="p-3 border-b flex items-center justify-between">
            <div className="font-semibold text-gray-700">Farmers with record ({farmersWithRecord.length})</div>
            <div className="text-xs text-gray-400">{loading ? "Loading…" : `${Object.keys(recordsMap).length} records`}</div>
          </div>

          <div>
            {farmersWithRecord.length === 0 ? (
              <div className="p-4 text-gray-500">No records for this date.</div>
            ) : (
              paginatedFarmersWithRecord.map((f) => {
                const entry = recordsMap[f.otherName];
                const rec = entry?.record || {};
                return (
                  <div key={f.otherName} className="p-3 border-b flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-800">{getFarmerDisplayName(f)}</div>
                      <div className="text-xs text-gray-500">{rec?.Details || `Total: ${rec?.TotalKgs || "0"}`}</div>
                      <div className="text-xs text-gray-400">{rec?.Date || selectedDate}</div>
                    </div>

                    <div className="text-right flex flex-col items-end gap-2">
                      <div className="font-semibold text-cyan-700">{Number(rec?.TotalKgs || 0).toFixed(1)} Kgs</div>
                      <div className="text-xs text-gray-500">{rec?.CollectedBy || "—"}</div>
                      <div className="flex gap-2 mt-2">
                        <button
                          className="px-3 py-1 bg-amber-500 text-white rounded text-sm"
                          onClick={() => handleEditRecord(f, entry)}
                        >
                          Edit
                        </button>
                        <button
                          className="px-3 py-1 bg-red-500 text-white rounded text-sm"
                          onClick={() => handleDeleteRecord(f, entry)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-3 py-2 border-t bg-slate-50">
            <div className="text-xs text-gray-500 mr-auto">
              {farmersWithRecord.length > 0
                ? `Showing ${withRecordsRange.start} - ${withRecordsRange.end} of ${farmersWithRecord.length}`
                : "No records"}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Rows</span>
              <select
                value={withRecordsPageSize}
                onChange={(e) => {
                  setWithRecordsPageSize(Number(e.target.value));
                  setWithRecordsPage(1);
                }}
                className="border rounded px-2 py-1 text-xs"
              >
                {MILK_COLLECTION_PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={`with-${size}`} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="px-2 py-1 rounded border text-xs disabled:opacity-50"
              onClick={() => setWithRecordsPage((page) => Math.max(1, page - 1))}
              disabled={withRecordsPage === 1}
            >
              Prev
            </button>
            <div className="text-xs text-gray-500">
              {withRecordsPage}/{withRecordsTotalPages}
            </div>
            <button
              className="px-2 py-1 rounded border text-xs disabled:opacity-50"
              onClick={() => setWithRecordsPage((page) => Math.min(withRecordsTotalPages, page + 1))}
              disabled={withRecordsPage >= withRecordsTotalPages}
            >
              Next
            </button>
          </div>
        </div>

        {/* Farmers without record (manual actions) */}
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <div className="p-3 border-b flex items-center justify-between">
            <div className="font-semibold text-gray-700">Farmers without record ({farmersWithoutRecordCount})</div>
            <div className="text-xs text-gray-400">Tap a farmer to add a record</div>
          </div>

          <div>
            {farmersWithoutRecordCount === 0 ? (
              <div className="p-4 text-gray-500">All farmers have records for this date.</div>
            ) : (
              paginatedFarmersWithoutRecord.map((f) => (
                <div
                  key={f.otherName}
                  className="p-3 border-b flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{getFarmerDisplayName(f)}</div>
                    <div className="text-xs text-gray-500">{getFarmerDisplayName(f)}</div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-1 bg-slate-100 text-gray-700 rounded text-sm border border-slate-200"
                      onClick={() => handleOpenHandModal(f)}
                    >
                      Handwritten
                    </button>
                    <button
                      className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded text-sm border border-indigo-200"
                      onClick={() => handleOpenVoiceModal(f)}
                    >
                      Voice
                    </button>
                    <button
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                      onClick={() => handleAddForFarmer(f)}
                    >
                      Add record
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-3 py-2 border-t bg-slate-50">
            <div className="text-xs text-gray-500 mr-auto">
              {farmersWithoutRecordCount > 0
                ? `Showing ${withoutRecordsRange.start} - ${withoutRecordsRange.end} of ${farmersWithoutRecordCount}`
                : "No records"}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Rows</span>
              <select
                value={withoutRecordsPageSize}
                onChange={(e) => {
                  setWithoutRecordsPageSize(Number(e.target.value));
                  setWithoutRecordsPage(1);
                }}
                className="border rounded px-2 py-1 text-xs"
              >
                {MILK_COLLECTION_PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={`without-${size}`} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="px-2 py-1 rounded border text-xs disabled:opacity-50"
              onClick={() => setWithoutRecordsPage((page) => Math.max(1, page - 1))}
              disabled={withoutRecordsPage === 1}
            >
              Prev
            </button>
            <div className="text-xs text-gray-500">
              {withoutRecordsPage}/{withoutRecordsTotalPages}
            </div>
            <button
              className="px-2 py-1 rounded border text-xs disabled:opacity-50"
              onClick={() => setWithoutRecordsPage((page) => Math.min(withoutRecordsTotalPages, page + 1))}
              disabled={withoutRecordsPage >= withoutRecordsTotalPages}
            >
              Next
            </button>
          </div>
        </div>

        {/* AI assistant accountant loop */}
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <div className="p-3 border-b flex items-center justify-between">
            <div className="font-semibold text-gray-700">AI assistant accountant ({ASSISTANT_NAME})</div>
            <div className="text-xs text-gray-400">Arrow keys: accept/decline, Space: skip</div>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-600">
              {ASSISTANT_NAME} reads farmers without records, captures your verbal milk amount, and pipes it into the add
              milk modal. Right arrow = accept & open modal, Left arrow = decline captured value, Space = move to next farmer.
            </p>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-2 text-sm rounded bg-cyan-600 text-white disabled:opacity-50"
                onClick={handleStartAssistant}
                disabled={!farmersWithoutRecordCount}
              >
                Start loop
              </button>
              <button
                className="px-3 py-2 text-sm rounded border border-gray-200 text-gray-700"
                onClick={handleStopAssistant}
              >
                Stop
              </button>
              <button
                className="px-3 py-2 text-sm rounded bg-indigo-100 text-indigo-700 border border-indigo-200 disabled:opacity-50"
                onClick={() => startListening("assistant")}
                disabled={!assistantActive || voiceStatus === "listening"}
              >
                {voiceStatus === "listening" && voiceTargetRef.current === "assistant" ? "Listening…" : "Capture verbally"}
              </button>
            </div>

            <div className="bg-gray-50 border rounded p-3">
              {assistantFarmer ? (
                <>
                  <div className="text-xs text-gray-500">Current farmer</div>
                  <div className="font-semibold text-gray-800">{getFarmerDisplayName(assistantFarmer)}</div>
                  <div className="text-xs text-gray-500 mb-2">{getFarmerDisplayName(assistantFarmer)}</div>
                  <label className="text-xs text-gray-600 block mb-1">Captured amount (edit if needed)</label>
                  <input
                    type="number"
                    className="w-full border rounded px-3 py-2"
                    value={assistantValue}
                    onChange={(e) => setAssistantValue(e.target.value)}
                    placeholder="e.g., 12.5"
                    min="0"
                    step="0.1"
                  />
                  <div className="text-xs text-gray-500 mt-2">
                    Press → to accept & open add modal, ← to discard, Space to skip to the next farmer.
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-500">No farmers without records for this date.</div>
              )}
            </div>
          </div>
        </div>

        {/* Handwriting loop (tablet friendly) */}
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <div className="p-3 border-b flex items-center justify-between">
            <div className="font-semibold text-gray-700">Handwriting capture loop</div>
            <div className="text-xs text-gray-400">Tablet ready • {HANDWRITING_SERVICE_LABEL}</div>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-600">
              Loop through farmers without a record and paste/enter the value converted from handwritten input
              (processed by the {HANDWRITING_SERVICE_DESC}). Use the button to push the value into the add record
              modal just like speech.
            </p>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-2 text-sm rounded bg-emerald-600 text-white disabled:opacity-50"
                onClick={handleStartHandLoop}
                disabled={!farmersWithoutRecordCount}
              >
                Start handwriting loop
              </button>
              <button
                className="px-3 py-2 text-sm rounded border border-gray-200 text-gray-700"
                onClick={handleStopHandLoop}
              >
                Stop
              </button>
              <button
                className="px-3 py-2 text-sm rounded bg-slate-100 text-gray-700 border border-slate-200 disabled:opacity-50"
                onClick={() => setHandLoopIndex((prev) => nextLoopIndex(prev, farmersWithoutRecordCount))}
                disabled={!handLoopActive || !farmersWithoutRecordCount}
              >
                Next farmer
              </button>
            </div>

            <div className="bg-gray-50 border rounded p-3">
              {handLoopFarmer ? (
                <>
                  <div className="text-xs text-gray-500">Current farmer</div>
                  <div className="font-semibold text-gray-800">{getFarmerDisplayName(handLoopFarmer)}</div>
                  <div className="text-xs text-gray-500 mb-2">{getFarmerDisplayName(handLoopFarmer)}</div>
                  <label className="text-xs text-gray-600 block mb-1">Handwriting/OCR amount</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={handLoopValue}
                    onChange={(e) => setHandLoopValue(e.target.value)}
                    placeholder="Paste recognized text or type amount"
                  />
                  <div className="flex justify-end mt-3 gap-2">
                    <button
                      className="px-3 py-2 text-sm bg-green-600 text-white rounded"
                      onClick={handleHandLoopSend}
                    >
                      Send to add modal
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Use a stylus/handwriting keypad on tablet, then tap “Send to add modal” to prefill the record.
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-500">No farmers without records for this date.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Record modal (add or edit). onSaved reloads lists */}
      {openModal && (
        <RecordMilkModal
          farmer={modalFarmer}
          companyName={companyName}
          selectedDate={selectedDate}
          existingRecord={modalExisting}
          onClose={() => {
            setOpenModal(false);
            setModalPrefillTotal(null);
          }}
          onSaved={onModalSaved}
          prefillTotalKgs={modalPrefillTotal}
        />
      )}

      {handModalFarmer && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm text-gray-500">Handwritten entry</div>
                <div className="font-semibold text-gray-800">{getFarmerDisplayName(handModalFarmer)}</div>
                <div className="text-xs text-gray-500">{getFarmerDisplayName(handModalFarmer)}</div>
              </div>
              <button className="text-sm text-gray-500" onClick={handleCloseHandModal}>
                Close
              </button>
            </div>

            <label className="text-xs text-gray-600 block mb-1">Milk quantity (Kgs)</label>
            <input
              type="number"
              className="w-full border rounded px-3 py-2 mb-4"
              value={handValue}
              onChange={(e) => setHandValue(e.target.value)}
              placeholder="e.g., 12.5"
              min="0"
              step="0.1"
            />

            <div className="text-xs text-gray-500 mb-3">
              Confirm the handwritten amount, then continue to the standard add record flow.
            </div>

            <div className="flex justify-end gap-2">
              <button className="px-3 py-2 text-sm text-gray-700" onClick={handleCloseHandModal}>
                Cancel
              </button>
              <button
                className="px-3 py-2 text-sm bg-green-600 text-white rounded"
                onClick={() => continueWithValue(handModalFarmer, handValue)}
              >
                Continue to add record
              </button>
            </div>
          </div>
        </div>
      )}

      {voiceModalFarmer && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm text-gray-500">Voice capture entry</div>
                <div className="font-semibold text-gray-800">{getFarmerDisplayName(voiceModalFarmer)}</div>
                <div className="text-xs text-gray-500">{getFarmerDisplayName(voiceModalFarmer)}</div>
              </div>
              <button className="text-sm text-gray-500" onClick={handleCloseVoiceModal}>
                Close
              </button>
            </div>

            <div className="mb-3">
              <div className="text-xs text-gray-600 mb-1">Use voice capture to dictate the milk amount. Speak a number to capture.</div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-2 text-sm rounded bg-indigo-600 text-white disabled:opacity-50"
                  onClick={() => startListening("voiceModal")}
                  disabled={voiceStatus === "listening"}
                >
                  {voiceStatus === "unsupported" ? "Not supported" : voiceStatus === "listening" ? "Listening…" : "Start listening"}
                </button>
                <button
                  className="px-3 py-2 text-sm rounded border border-gray-200 text-gray-700"
                  onClick={stopListening}
                  disabled={voiceStatus !== "listening"}
                >
                  Stop
                </button>
                <div className="text-xs text-gray-500">{voiceStatus === "listening" ? "Awaiting response…" : voiceStatus === "error" ? "Recognition error" : ""}</div>
              </div>
            </div>

            <label className="text-xs text-gray-600 block mb-1">Captured milk quantity (editable)</label>
            <input
              type="number"
              className="w-full border rounded px-3 py-2 mb-4"
              value={voiceValue}
              onChange={(e) => setVoiceValue(e.target.value)}
              placeholder="e.g., 10.5"
              min="0"
              step="0.1"
            />

            <div className="text-xs text-gray-500 mb-3">
              Confirm the captured amount, then continue to the standard add record flow.
            </div>

            <div className="flex justify-end gap-2">
              <button className="px-3 py-2 text-sm text-gray-700" onClick={handleCloseVoiceModal}>
                Cancel
              </button>
              <button
                className="px-3 py-2 text-sm bg-green-600 text-white rounded"
                onClick={() => continueWithValue(voiceModalFarmer, voiceValue)}
              >
                Continue to add record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilkCollectionTab;
